import { FC, useMemo } from 'react';
import { Tooltip } from '@ibsys/kit';
import { formatTargetRate } from '@/shared/lib/utils';
import { cn } from '@/shared/lib/utils';
import { RELIABLE_MIN_DIALOGS, getCellBackgroundStyle } from '../lib/heatmapColors';

/** Максимальная длина подписи столбца (вертикальной): длиннее — усечение с «…»,
 *  чтобы шапка матрицы не была чрезмерно высокой. Полный текст — в тултипе. */
const MAX_COL_LABEL = 26;

/** Элемент оси (строки или столбца): стабильный id и подпись. */
export type HeatmapAxisItem = { id: string; label: string | null };

/** Ячейка тепловой матрицы, адресуемая парой (rowId, colId). */
export type HeatmapCell = {
  rowId: string;
  colId: string;
  /** Объём диалогов в ячейке */
  nDialogs: number;
  /** Доля целевого исхода (0..1) или null */
  targetRate: number | null;
};

/** Локализованные подписи тепловой матрицы (namespace-независимые). */
export type HeatmapLabels = {
  /** Подпись угловой ячейки (напр. «Строка \ Столбец») */
  corner: string;
  /** Подпись маргинала-итога */
  total: string;
  /** Название сущности-строки в тултипе */
  rowName: string;
  /** Название сущности-столбца в тултипе */
  colName: string;
  /** Подпись «Диалогов» в тултипе */
  dialogs: string;
  /** Подпись «Доля цели» в тултипе */
  targetRate: string;
  /** Пояснение для ненадёжной ячейки */
  unreliableHint: string;
};

type Props = {
  /** Строки матрицы */
  rows: HeatmapAxisItem[];
  /** Столбцы матрицы */
  columns: HeatmapAxisItem[];
  /** Ячейки матрицы */
  cells: HeatmapCell[];
  /** Базовая доля цели по выборке (0..1) или null */
  baselineRate: number | null;
  /** Локализованные подписи */
  labels: HeatmapLabels;
  /** Клик по подписи строки (id строки = conceptId); если задан — подпись строки кликабельна */
  onRowClick?: (id: string) => void;
  /** Клик по подписи столбца (id столбца = conceptId); если задан — подпись столбца кликабельна */
  onColClick?: (id: string) => void;
  /** Клик по непустой ячейке (rowId, colId); если задан — ячейки кликабельны */
  onCellClick?: (rowId: string, colId: string) => void;
};

/** Агрегаты маргинала (по строке или столбцу): объём и взвешенная средняя доля цели. */
type Marginal = { volume: number; avgRate: number | null };

/** Прочерк на месте значения, которого нет: пустая ячейка, безымянная ось. */
const EMPTY_VALUE = '—';

/** Ключ ячейки по паре идентификаторов. */
const cellKey = (rowId: string, colId: string) => `${rowId}__${colId}`;

/** Считает взвешенную по объёму среднюю долю цели для набора ячеек. */
const computeMarginal = (cells: HeatmapCell[]): Marginal => {
  let volume = 0;
  let weightedSum = 0;
  let ratedVolume = 0;
  cells.forEach((c) => {
    volume += c.nDialogs;
    if (c.targetRate !== null) {
      weightedSum += c.targetRate * c.nDialogs;
      ratedVolume += c.nDialogs;
    }
  });
  return { volume, avgRate: ratedVolume > 0 ? weightedSum / ratedVolume : null };
};

/**
 * Универсальная тепловая матрица «строка × столбец». Цвет ячейки — отклонение
 * доли цели от baseline, число внутри — объём диалогов. Ненадёжные ячейки
 * (малый n) приглушены и помечены. По краям — маргиналы (объём и средняя доля
 * цели), в углу — общий объём. Подписи столбцов развёрнуты вертикально, чтобы
 * читались целиком. Широкая матрица скроллится по горизонтали.
 */
export const HeatmapMatrix: FC<Props> = ({ rows, columns, cells, labels, onRowClick, onColClick, onCellClick }) => {
  const cellsMap = useMemo(() => {
    const map = new Map<string, HeatmapCell>();
    cells.forEach((c) => map.set(cellKey(c.rowId, c.colId), c));
    return map;
  }, [cells]);

  const rowMarginals = useMemo(() => {
    const map = new Map<string, Marginal>();
    rows.forEach((row) => {
      const rowCells = columns
        .map((col) => cellsMap.get(cellKey(row.id, col.id)))
        .filter((c): c is HeatmapCell => c !== undefined);
      map.set(row.id, computeMarginal(rowCells));
    });
    return map;
  }, [rows, columns, cellsMap]);

  const columnMarginals = useMemo(() => {
    const map = new Map<string, Marginal>();
    columns.forEach((col) => {
      const colCells = rows
        .map((row) => cellsMap.get(cellKey(row.id, col.id)))
        .filter((c): c is HeatmapCell => c !== undefined);
      map.set(col.id, computeMarginal(colCells));
    });
    return map;
  }, [rows, columns, cellsMap]);

  const totalVolume = useMemo(() => cells.reduce((acc, c) => acc + c.nDialogs, 0), [cells]);

  // Максимум целевой доли по НАДЁЖНЫМ ячейкам — верх шкалы расцветки (полный акцент).
  const maxRate = useMemo(() => {
    let m = 0;
    cells.forEach((c) => {
      if (c.nDialogs >= RELIABLE_MIN_DIALOGS && c.targetRate !== null && c.targetRate > m) {
        m = c.targetRate;
      }
    });
    return m;
  }, [cells]);

  const marginalCellClass = 'border border-border bg-muted/40 px-2 py-1 text-center align-middle whitespace-nowrap';

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            <th className="sticky left-0 top-0 z-20 min-w-40 border border-border bg-card px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              {labels.corner}
            </th>
            {columns.map((col) => (
              <th
                key={col.id}
                className="sticky top-0 z-10 min-w-16 border border-border bg-card px-1 py-2 text-center align-bottom text-xs font-medium text-foreground"
              >
                {/* Подпись столбца развёрнута вертикально (снизу вверх). Длинные подписи
                    усекаются до MAX_COL_LABEL символов, чтобы шапка не была слишком высокой;
                    полный текст доступен в тултипе. */}
                <Tooltip tooltip={col.label ?? EMPTY_VALUE}>
                  <span
                    className={cn(
                      'inline-block rotate-180 whitespace-nowrap [writing-mode:vertical-rl]',
                      onColClick && 'cursor-pointer hover:text-primary',
                    )}
                    {...(onColClick && {
                      role: 'button',
                      tabIndex: -1,
                      onClick: () => onColClick(col.id),
                    })}
                  >
                    {col.label && col.label.length > MAX_COL_LABEL
                      ? `${col.label.slice(0, MAX_COL_LABEL - 1)}…`
                      : (col.label ?? EMPTY_VALUE)}
                  </span>
                </Tooltip>
              </th>
            ))}
            <th className={cn(marginalCellClass, 'sticky top-0 z-10 text-xs font-medium text-muted-foreground')}>
              {labels.total}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rowMarginal = rowMarginals.get(row.id);
            return (
              <tr key={row.id}>
                <th
                  scope="row"
                  className="sticky left-0 z-10 min-w-40 max-w-56 border border-border bg-card px-3 py-2 text-left text-xs font-medium text-foreground"
                >
                  <Tooltip tooltip={row.label ?? EMPTY_VALUE}>
                    <span
                      className={cn('line-clamp-2', onRowClick && 'cursor-pointer hover:text-primary')}
                      {...(onRowClick && {
                        role: 'button',
                        tabIndex: -1,
                        onClick: () => onRowClick(row.id),
                      })}
                    >
                      {row.label ?? EMPTY_VALUE}
                    </span>
                  </Tooltip>
                </th>
                {columns.map((col) => {
                  const cell = cellsMap.get(cellKey(row.id, col.id));
                  if (!cell || cell.nDialogs === 0) {
                    return (
                      <td key={col.id} className="border border-border p-0 text-center align-middle">
                        <span className="block px-2 py-2 text-xs text-muted-foreground/50">{EMPTY_VALUE}</span>
                      </td>
                    );
                  }
                  const isUnreliable = cell.nDialogs < RELIABLE_MIN_DIALOGS;
                  const tooltip = (
                    <div className="flex flex-col gap-0.5 text-xs">
                      <span>{`${labels.rowName}: ${row.label ?? EMPTY_VALUE}`}</span>
                      <span>{`${labels.colName}: ${col.label ?? EMPTY_VALUE}`}</span>
                      <span>{`${labels.dialogs}: ${cell.nDialogs}`}</span>
                      <span>{`${labels.targetRate}: ${formatTargetRate(cell.targetRate)}`}</span>
                      {isUnreliable && <span>{labels.unreliableHint}</span>}
                    </div>
                  );
                  return (
                    <td key={col.id} className="border border-border p-0 text-center align-middle">
                      <Tooltip tooltip={tooltip}>
                        <div
                          className={cn(
                            'flex min-w-16 items-center justify-center px-2 py-2 text-xs font-medium',
                            isUnreliable ? 'bg-muted/40 text-muted-foreground' : 'text-foreground',
                            onCellClick && 'cursor-pointer hover:brightness-95 hover:outline hover:outline-primary',
                          )}
                          style={isUnreliable ? undefined : getCellBackgroundStyle(cell.targetRate, maxRate)}
                          {...(onCellClick && {
                            role: 'button',
                            tabIndex: -1,
                            onClick: () => onCellClick(row.id, col.id),
                          })}
                        >
                          {cell.nDialogs}
                          {isUnreliable && '*'}
                        </div>
                      </Tooltip>
                    </td>
                  );
                })}
                <td className={marginalCellClass}>
                  <div className="flex flex-col text-xs">
                    <span className="font-medium text-foreground">{rowMarginal?.volume ?? 0}</span>
                    <span className="text-muted-foreground">{formatTargetRate(rowMarginal?.avgRate)}</span>
                  </div>
                </td>
              </tr>
            );
          })}
          <tr>
            <th
              scope="row"
              className={cn(
                marginalCellClass,
                'sticky left-0 z-10 text-left text-xs font-medium text-muted-foreground',
              )}
            >
              {labels.total}
            </th>
            {columns.map((col) => {
              const colMarginal = columnMarginals.get(col.id);
              return (
                <td key={col.id} className={marginalCellClass}>
                  <div className="flex flex-col text-xs">
                    <span className="font-medium text-foreground">{colMarginal?.volume ?? 0}</span>
                    <span className="text-muted-foreground">{formatTargetRate(colMarginal?.avgRate)}</span>
                  </div>
                </td>
              );
            })}
            <td className={cn(marginalCellClass, 'font-semibold text-foreground')}>{totalVolume}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
